<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <colgroup>
                <col style="width: 8%;" />
                <col style="width: 22%;" />
                <col style="width: 8%;" />
                <col style="width: 7%;" />
                <col style="width: 15%;" />
                <col v-if="!enableIntegratedPayments" style="width: 28%;" />
                <col v-if="enableIntegratedPayments" style="width: 14%;" />
                <col v-if="enableIntegratedPayments" style="width: 14%;" />
                <col style="width: 8%;" />
                <col style="width: 4%;" />
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th>Refund:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Return:</b-th>
                    <b-th>Amount:</b-th>
                    <b-th>Type:</b-th>
                    <b-th>Notes:</b-th>
                    <b-th v-if="enableIntegratedPayments">Transaction ID:</b-th>
                    <b-th>Date:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(refund, i) in refunds" :key="i">
                    <b-td class="text-nowrap">
                        <span
                            v-if="
                                _hasAccess('billing.refund.update') &&
                                refund.is_active_actor &&
                                refund.refund_type
                            "
                            class="text-link"
                            @click="openRefundModal(refund)"
                            >{{ refund.name }}
                        </span>
                        <span v-else>{{ refund.name }}</span>
                    </b-td>
                    <b-td>
                        <client-name
                            v-if="!isStringDeleted(refund.client_name)"
                            :client="generateClientObject(refund)"
                        />
                    </b-td>
                    <b-td class="text-nowrap">
                        <return-link
                            v-if="refund.return_encoded_id"
                            :return="generateReturnObject(refund)"
                            :text="refund.return_name"
                        />
                    </b-td>
                    <b-td>
                        {{ _toCurrency(refund.amount) }}
                    </b-td>
                    <b-td>
                        {{ refund.refund_type ? refund.refund_type.name : "" }}
                    </b-td>
                    <b-td>
                        {{ refund.note }}
                    </b-td>
                    <b-td v-if="enableIntegratedPayments">
                        <span
                            v-if="
                                (refund.cc_transaction == 'STRIPE' ||
                                    refund.cc_transaction == 'CCONNECT') &&
                                refund.transaction_id
                            "
                        >
                            <transaction-id-link
                                v-for="transactionId in getTransactionIds(
                                    refund.transaction_id
                                )"
                                :key="transactionId"
                                :transactionId="transactionId"
                                :transactionType="refund.cc_transaction"
                                :account="refundAccount"
                            />
                        </span>
                        <span v-else>
                            -
                        </span>
                    </b-td>
                    <b-td class="text-nowrap">
                        {{ _carbonDateDisplay(refund.refund_date) }}
                    </b-td>
                    <b-td class="text-right" v-if="refund.status == 0">
                        <span
                            class="badge bg-secondary"
                            style="font-weight: bold;"
                            v-b-tooltip.hover.top
                            title="The customer's bank is processing this payment"
                            >Pending <i class="fas fa-clock"></i
                        ></span>
                    </b-td>
                    <b-td
                        class="text-right"
                        v-else-if="
                            refund.refund_type &&
                            !isCardConnectTerminal(refund.refund_type.name) &&
                            refund.is_active_actor
                        "
                    >
                        <b-dropdown
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                            v-if="
                                _hasAccess('billing.refund.update') ||
                                _hasAccess('billing.credit.create')
                            "
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                href="#"
                                @click="openRefundModal(refund)"
                                v-if="_hasAccess('billing.refund.update')"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Refund
                            </b-dropdown-item>
                            <b-dropdown-item
                                href="#"
                                @click="openConvertToCreditModal(refund)"
                                v-if="
                                    _hasAccess('billing.credit.create') &&
                                    isNotIntergratedPayment(refund.type)
                                "
                            >
                                <i class="cus-arrow-refresh"></i>
                                Convert to Credit
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="refunds.length === 0">
                    <b-td :colspan="enableIntegratedPayments ? 9 : 8">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getRefunds"
        />
        <refund-convert-credit
            ref="refundConvertCredit"
            modalId="refundConvertCredit"
            ok-title="Save"
            modal-ref="refundConvertCredit"
            :client-name="selectedClientname"
            :refund="selectedRefund"
            @reload="getRefunds(meta.currentPage)"
        />

        <refund-edit-modal
            modal-ref="refundEditModal"
            ref="refundEditModal"
            :refund="selectedRefund"
            :client-name="selectedClientname"
            @reload="getRefunds(meta.currentPage)"
        />
    </div>
</template>

<script>
/**
 * @name RefundList
 * @description List of refund
 * @see apps/application/views/staff/billing/sub/refund/list.php
 */
import Loader from "@/components/elements/loader-list";
import BillingRefundServices from "@/services/billing/billing-refund";
import Payment from "@/services/payment";
import TransactionIdLink from "@/components/commons/transaction-id-link";
import EXTENSIONS from "@/constants/extensions";
const NAME_CCONNECT_TERMINAL = "Credit Card On Terminal (CardConnect)";

export default {
    mixins: {},
    components: {
        TransactionIdLink,
        Loader,
        ClientName: () => import("@/components/commons/client-name"),
        ReturnLink: () => import("@/components/commons/return-link"),
        RefundEditModal: () =>
            import("@/components/client-refunds/refund-edit"),
        RefundConvertCredit: () =>
            import("@/components/client-refunds/refund-convert")
    },
    props: {},
    data() {
        return {
            isLoading: false,
            refunds: [],
            selectedRefund: {},
            selectedClientname: null,
            ccPaymentTypes: [],
            meta: { currentPage: 1 },
            refundAccount: null,
            enableIntegratedPayments: false
        };
    },
    async mounted() {
        await this.getPaymentTypes();
        this.getPaymentAccounts();
        this.getRefunds();
    },
    methods: {
        openRefundModal(refund) {
            this.selectedRefund = Object.assign(
                { isHasReeturn: refund.return_encoded_id !== false },
                refund
            );
            this.selectedClientname = refund.client_name;
            this.$store.dispatch("checkBillingLock", {
                date: this.selectedRefund.refund_date
                    ? this._fromDateCarbon(
                          this.selectedRefund.refund_date
                      ).toFormat("X")
                    : null,
                onProceed: () => this.$refs["refundEditModal"].open(),
                onError: message => this._message(message, "danger")
            });
        },
        openConvertToCreditModal(refund) {
            this.selectedRefund = Object.assign({}, refund);
            this.selectedClientname = refund.client_name;
            this.selectedClientname = this.selectedRefund.client_name;
            this.$store.dispatch("checkBillingLock", {
                date: refund.refund_date
                    ? this._fromDateCarbon(refund.refund_date).toFormat("X")
                    : null,
                onProceed: () => this.$refs["refundConvertCredit"].open(refund),
                onError: message => this._message(message, "danger")
            });
        },
        getRefunds(page = 1) {
            let self = this;
            self.refunds = [];
            self.isLoading = true;
            BillingRefundServices.getRefunds(page, self.PER_PAGE)
                .then(res => {
                    self.refunds = res.data;
                    self.meta = {
                        lastPage: res.last_page,
                        currentPage: res.current_page,
                        total: res.total,
                        perPage: res.per_page
                    };
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        generateClientObject(refund) {
            return {
                encoded_id: refund.client_encoded_id,
                id: refund.client_id,
                label: refund.client_name
            };
        },
        generateReturnObject(refund) {
            return {
                encoded_id: refund.return_encoded_id,
                id: refund.return_id
            };
        },
        getPaymentTypes() {
            Payment.getCreditCardPayments().then(response => {
                this.ccPaymentTypes = response.resources;
            });
        },
        isNotIntergratedPayment(refundTypeId) {
            let matchIndex = _.findIndex(this.ccPaymentTypes, function (
                element
            ) {
                return element.id == refundTypeId;
            });
            return matchIndex == -1;
        },
        isCardConnectTerminal(refundType) {
            if (refundType) {
                return (
                    refundType.toLowerCase().replace(/\s+/g, "") ==
                    NAME_CCONNECT_TERMINAL.toLowerCase().replace(/\s+/g, "")
                );
            }
            return true;
        },
        isStringDeleted(clientName) {
            // if client name only show deleted, dont show the client name
            return clientName.toLowerCase().replace(/\s+/g, "") == "(deleted)";
        },
        getPaymentAccounts() {
            let self = this;

            let { CODE } = EXTENSIONS;
            self.enableIntegratedPayments =
                self._isExtensionEnabled(CODE.CODE_CCONNECT) ||
                self._isExtensionEnabled(CODE.CODE_GRAVITY) ||
                self._isExtensionEnabled(CODE.CODE_STRIPE);

            Payment.getPaymentAccounts(this._setting.business.id)
                .then(response => {
                    if (response.message.code == 200) {
                        self.refundAccount =
                            response.message.payment_processor_id;
                    } else {
                        self._message(response.message.description, "danger");
                        self.refundAccount = null;
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getTransactionIds(transactionIdString) {
            return transactionIdString.split(",");
        }
    }
};
</script>
